import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Layout,
  message,
  Row,
  Select,
  Upload,
} from "antd";
import { Trash, Plus, Inbox, Eye, CheckCircle, XCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import ConfirmationModal from "../../Components/CustomModals/ConfirmationModal";
import {
  formatNumbers,
  formatString,
  navigateTo,
} from "../../Utils/Common";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useParams } from "react-router";

const { Dragger } = Upload;

interface PaymentRequestFormProps {
  add?: boolean;
  edit?: boolean;
  view?: boolean;
  approval?: boolean;
}

const PaymentRequestForm: React.FC<PaymentRequestFormProps> = ({
  add,
  edit,
  view,
  approval,
}) => {
  const [loading,           setLoading]           = useState("");
  const [showModal,         setShowModal]         = useState("");
  const [disapprovalReason, setDisapprovalReason] = useState("");
  const [purchaseOrders,    setPurchaseOrders]    = useState<any[]>([]);
  const [form]                                    = Form.useForm();

  const itemsArray = Form.useWatch("itemsArray", form);
  const { id: payment_request_id } = useParams<{ id: string }>();

  const isReadOnly = view || approval;

  function clearFields() {
    form.resetFields();
  }

  const grandTotal = (itemsArray || []).reduce((sum: number, item: any) => {
    return sum + (Number(item?.grand_total) || 0);
  }, 0);

  // ─── Upload ───────────────────────────────────────────────────────────────────
  const handleBeforeUpload = (file: any) => {
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) message.error(`${file.name} exceeds the 2MB size limit.`);
    return isLt2M ? false : Upload.LIST_IGNORE;
  };

  // ─── Submit (add / edit) ──────────────────────────────────────────────────────
  async function submitForm(data: any) {
    const formattedItemsArray = (data.itemsArray || []).map((item: any) => ({
      ...item,
      expense_date: item.expense_date
        ? dayjs(item.expense_date).format("YYYY-MM-DD")
        : "",
    }));

    const purchase_order_ids = (data.itemsArray || [])
      .map((item: any) => item.po_id)
      .filter(Boolean);

    const formattedData: any = {
      ...data,
      itemsArray:         formattedItemsArray,
      grand_total:        grandTotal,
      purchase_order_ids: JSON.stringify(purchase_order_ids),
    };

    if (edit) formattedData.payment_request_id = payment_request_id;

    const formData = new FormData();
    for (const key in formattedData) {
      if (key === "itemsArray") {
        formData.append(key, JSON.stringify(formattedData[key]));
      } else if (key === "attachment") {
        (formattedData[key] || []).forEach((file: any) => {
          if (file.originFileObj) formData.append("attachments[]", file.originFileObj);
        });
      } else {
        const value = formattedData[key];
        if (value !== null && value !== undefined) formData.append(key, value);
      }
    }

    setLoading("submit");
    const response = await postRequest(
      add ? "payment_requests/create" : "payment_requests/update",
      formData,
      true
    );

    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => { clearFields(); setLoading(""); navigateTo("/payment-request"); }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${add ? "create" : "update"} payment request.`
      );
    }
  }

  // ─── Approve ──────────────────────────────────────────────────────────────────
  async function approveRequest() {
    setLoading("approve");
    const response = await postRequest(
      "payment_requests/change_status",
      { payment_request_id, action: "approved" },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => { clearFields(); setShowModal(""); setLoading(""); navigateTo("/payment-request"); }, 1000);
    } else {
      setLoading(""); setShowModal("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to approve payment request."
      );
    }
  }

  // ─── Disapprove ───────────────────────────────────────────────────────────────
  async function disapproveRequest() {
    if (!disapprovalReason.trim()) {
      message.warning("Please provide a reason for disapproval.");
      return;
    }
    setLoading("disapprove");
    const response = await postRequest(
      "payment_requests/change_status",
      { payment_request_id, action: "disapproved", reason: disapprovalReason },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => { clearFields(); setShowModal(""); setLoading(""); navigateTo("/payment-request"); }, 1000);
    } else {
      setLoading(""); setShowModal("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to disapprove payment request."
      );
    }
  }

  // ─── Fetch ────────────────────────────────────────────────────────────────────
  async function fetchPurchaseOrders() {
    setPurchaseOrders([]);
    setLoading("fetch");
    const response = await getRequest(`purchase_orders/get`, { status: "approved" }, true);
    if (response.success) setPurchaseOrders(response.data.data);
    setLoading("");
  }

  async function fetchPaymentRequestData() {
    if (!payment_request_id) return;
    setLoading("fetch");
    const response = await getRequest("payment_requests/get", { payment_request_id }, true);
    if (response.success) {
      const data  = response.data.data[0];
      const items = (data.orders || []).map((order: any) => ({
        po_id:         order.purchase_order_id,
        expense_date:  order.expense_date ? dayjs(order.expense_date) : null,
        supplier_name: order.supplier_name,
        branch_name:   order.branch_name,
        grand_total:   order.grand_total,
      }));
      form.setFieldsValue({ itemsArray: items });
    }
    setLoading("");
  }

  // ─── PO row handlers ──────────────────────────────────────────────────────────
  function handleSelectPO(selectedId: any, fieldName: any) {
    const selectedPO = purchaseOrders.find((po: any) => po.id === selectedId);
    if (selectedPO) {
      form.setFields([
        { name: ["itemsArray", fieldName, "expense_date"],  value: dayjs(selectedPO.expense_date) },
        { name: ["itemsArray", fieldName, "supplier_name"], value: selectedPO.supplier_name },
        { name: ["itemsArray", fieldName, "branch_name"],   value: selectedPO.branch_name },
        { name: ["itemsArray", fieldName, "grand_total"],   value: selectedPO.grand_total },
      ]);
    }
  }

  function handleClearPO(fieldName: any) {
    form.setFields([
      { name: ["itemsArray", fieldName, "expense_date"],  value: null },
      { name: ["itemsArray", fieldName, "supplier_name"], value: "" },
      { name: ["itemsArray", fieldName, "branch_name"],   value: "" },
      { name: ["itemsArray", fieldName, "grand_total"],   value: null },
    ]);
  }

  // ─── Effects ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchPurchaseOrders();
    if (edit || view || approval) fetchPaymentRequestData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Layout className="page">
      <Helmet><title>Agua Lab ERP System</title></Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title={
                add      ? "Add Payment Request"    :
                edit     ? "Edit Payment Request"   :
                approval ? "Approve Payment Request" :
                           "View Payment Request"
              }
              withDateRangeFilter={false}
              withAddButton={false}
              withSearchBtn={false}
            />
          </Col>

          <div className="d-flex w-full form-container">
            <Col span={24}>
              <Form form={form} onFinish={submitForm} layout="vertical">
                <Row className="w-full" gutter={[10, 10]}>

                  {/* ── PO List ──────────────────────────────── */}
                  <Col xs={24}>
                    <span className="edit-label mb-2">
                      <label className="text-red-500 me-1">*</label>List of Purchase Orders:
                    </span>
                    <Form.List name="itemsArray" initialValue={[{}]}>
                      {(fields, { add: addItem, remove }) => (
                        <>
                          <Col xs={24}>
                            <div className="overflow-auto max-h-[600px]">
                              <table className="w-full border border-collapse min-w-[800px]">
                                <thead>
                                  <tr className="bg-primary text-white font-bold border">
                                    <th className="p-2 border w-[120px]">PO No.</th>
                                    <th className="p-2 border w-[150px]">Transaction Date</th>
                                    <th className="p-2 border w-[150px]">Supplier Name</th>
                                    <th className="p-2 border w-[150px]">Branch</th>
                                    <th className="p-2 border w-[120px]">Amount Payable</th>
                                    <th className="p-2 border w-[80px] text-center">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {fields.map((field) => (
                                    <tr key={field.key} className="border bg-white">
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "po_id"]} rules={[{ required: true, message: "Required" }]} noStyle>
                                          <Select
                                            className="w-full"
                                            placeholder="Select PO"
                                            showSearch
                                            allowClear
                                            disabled={isReadOnly}
                                            onSelect={(value) => handleSelectPO(value, field.name)}
                                            onClear={() => handleClearPO(field.name)}
                                            filterOption={(input: string, option?: any) =>
                                              option?.children?.toLowerCase().includes(input.toLowerCase())
                                            }
                                          >
                                            {purchaseOrders
                                              .filter((po: any) =>
                                                !form.getFieldValue("itemsArray")?.some(
                                                  (item: any, index: number) =>
                                                    item?.po_id === po.id && index !== field.name
                                                )
                                              )
                                              .map((po: any) => (
                                                <Select.Option key={po.id} value={po.id}>
                                                  {po.id}
                                                </Select.Option>
                                              ))}
                                          </Select>
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "expense_date"]} noStyle>
                                          <DatePicker className="w-full" disabled />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "supplier_name"]} noStyle>
                                          <Input disabled />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "branch_name"]} noStyle>
                                          <Input disabled />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "grand_total"]} noStyle>
                                          <Input type="number" disabled />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border text-center">
                                        {form.getFieldValue("itemsArray")?.[field.name]?.po_id && (
                                          <Button
                                            type="text"
                                            icon={<Eye color="#557ac3" size={17} />}
                                            onClick={() => {
                                              const poId = form.getFieldValue("itemsArray")?.[field.name]?.po_id;
                                              window.open(`/purchase-order/view/${poId}`, "_blank");
                                            }}
                                          />
                                        )}
                                        {!isReadOnly && fields.length > 1 && (
                                          <Button
                                            type="text"
                                            danger
                                            icon={<Trash size={17} />}
                                            onClick={() => remove(field.name)}
                                          />
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Col>

                          <Col xs={24} className="mt-2">
                            <Row justify="space-between" align="middle">
                              <Col>
                                {!isReadOnly && (
                                  <Button type="primary" onClick={() => addItem()} icon={<Plus size={16} />}>
                                    Add PO
                                  </Button>
                                )}
                              </Col>
                              <Col className="text-end">
                                <strong>Grand Total: ₱ {formatNumbers(grandTotal)}</strong>
                              </Col>
                            </Row>
                          </Col>
                        </>
                      )}
                    </Form.List>
                  </Col>

                  {/* ── Attachment ───────────────────────────── */}
                  {!view && (
                    <Col xs={24} className="mt-5 pt-5">
                      <Form.Item
                        name="attachment"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}
                      >
                        <label className="text-red-500 me-1">*</label>Attachment
                        <Dragger name="file" beforeUpload={handleBeforeUpload} accept=".pdf,image/*" disabled={isReadOnly}>
                          <p className="ant-upload-drag-icon flex justify-center items-center text-3xl"><Inbox /></p>
                          <p className="ant-upload-text">Click or drag file to upload</p>
                          <p className="ant-upload-hint">Supports PDF, Image formats.</p>
                        </Dragger>
                        <p className="text-textColor pt-2">Note: Maximum file size is 2MB per file.</p>
                      </Form.Item>
                    </Col>
                  )}

                  {/* ── Actions ──────────────────────────────── */}
                  <Col lg={24} className="mt-5 flex justify-end gap-3">
                    <Button onClick={() => { navigateTo("/payment-request"); clearFields(); }}>
                      {isReadOnly ? "Close" : "Cancel"}
                    </Button>

                    {(add || edit) && (
                      <Button htmlType="submit" type="primary" loading={loading === "submit"}>
                        {add ? "Submit" : "Update"}
                      </Button>
                    )}

                    {approval && (
                      <>
                        <Button
                          danger
                          loading={loading === "disapprove"}
                          onClick={() => { setDisapprovalReason(""); setShowModal("disapprove"); }}
                        >
                          Disapprove
                        </Button>
                        <Button
                          type="primary"
                          loading={loading === "approve"}
                          onClick={() => setShowModal("approve")}
                        >
                          Approve
                        </Button>
                      </>
                    )}
                  </Col>

                </Row>
              </Form>
            </Col>
          </div>
        </Row>
      </Layout>

      {/* ── Approve modal ─────────────────────────────────────────────────────── */}
      <ConfirmationModal
        title="Are you sure you want to approve this payment request?"
        icon={<CheckCircle className="text-green-500 mt-5" size={60} />}
        showModal={showModal === "approve"}
        onCancel={() => setShowModal("")}
        footer={
          <div className="flex justify-center gap-3 mt-5">
            <Button onClick={() => setShowModal("")}>Cancel</Button>
            <Button type="primary" loading={loading === "approve"} onClick={approveRequest}>
              Approve
            </Button>
          </div>
        }
      />

      {/* ── Disapprove modal ──────────────────────────────────────────────────── */}
      <ConfirmationModal
        title="Are you sure you want to disapprove this payment request?"
        icon={<XCircle className="text-red-500 mt-5" size={60} />}
        showModal={showModal === "disapprove"}
        onCancel={() => setShowModal("")}
        footer={
          <div className="flex flex-col gap-3 mt-5">
            <Input.TextArea
              rows={3}
              placeholder="Enter reason for disapproval..."
              value={disapprovalReason}
              onChange={(e) => setDisapprovalReason(e.target.value)}
            />
            <div className="flex justify-center gap-3">
              <Button onClick={() => setShowModal("")}>Cancel</Button>
              <Button danger loading={loading === "disapprove"} onClick={disapproveRequest}>
                Disapprove
              </Button>
            </div>
          </div>
        }
      />

      <PersistentFooter />
    </Layout>
  );
};

export default PaymentRequestForm;